import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/modules/auth/stores/authStore';

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isGuest } = useAuthStore();

  const handleStart = () => {
    if (isAuthenticated || isGuest) {
      navigate('/lobby');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-purple-600/20" />
        <div className="relative max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="text-6xl mb-4">🎙️</div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            말싸움
          </h1>
          <p className="text-xl text-slate-300 mb-2">
            실시간 음성 토론 배틀 게임
          </p>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            다양한 주제로 실시간 음성 토론을 펼치고,<br />
            배심원들의 투표로 승패를 가리세요!
          </p>
          <button
            onClick={handleStart}
            className="btn-primary text-lg px-8 py-4 rounded-xl shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 transition-all"
          >
            {isAuthenticated || isGuest ? '게임 시작하기' : '무료로 시작하기'} →
          </button>
        </div>
      </header>

      {/* How It Works */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-12">🎮 게임 방법</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-800/50 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-4">1️⃣</div>
              <h3 className="font-semibold text-lg mb-2">주제 선택</h3>
              <p className="text-slate-400 text-sm">
                다양한 시사, 사회, 문화 주제 중<br />
                관심 있는 주제와 입장을 선택하세요
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-4">2️⃣</div>
              <h3 className="font-semibold text-lg mb-2">매칭 & 토론</h3>
              <p className="text-slate-400 text-sm">
                반대 의견 상대와 자동 매칭!<br />
                실시간 음성으로 토론을 펼치세요
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-4">3️⃣</div>
              <h3 className="font-semibold text-lg mb-2">투표 & 결과</h3>
              <p className="text-slate-400 text-sm">
                배심원들이 공정하게 투표!<br />
                승리하면 경험치와 티어 상승
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 bg-slate-800/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-12">✨ 주요 기능</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex gap-4 p-4">
              <div className="text-3xl">🎤</div>
              <div>
                <h3 className="font-semibold mb-1">실시간 음성 토론</h3>
                <p className="text-slate-400 text-sm">
                  텍스트가 아닌 음성으로 직접 토론하세요. 
                  더 생생하고 역동적인 토론이 가능합니다.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4 p-4">
              <div className="text-3xl">👨‍⚖️</div>
              <div>
                <h3 className="font-semibold mb-1">배심원 시스템</h3>
                <p className="text-slate-400 text-sm">
                  제3자인 배심원들이 토론을 듣고 공정하게 투표합니다.
                  객관적인 승패 판정이 가능합니다.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4 p-4">
              <div className="text-3xl">🏆</div>
              <div>
                <h3 className="font-semibold mb-1">티어 & 랭킹</h3>
                <p className="text-slate-400 text-sm">
                  브론즈부터 다이아몬드까지! 승리할수록 
                  티어가 올라가고 실력을 증명할 수 있습니다.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4 p-4">
              <div className="text-3xl">📱</div>
              <div>
                <h3 className="font-semibold mb-1">모바일 최적화</h3>
                <p className="text-slate-400 text-sm">
                  스마트폰에서도 쾌적하게 플레이!
                  언제 어디서나 토론에 참여하세요.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topics Preview */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-4">🔥 인기 토론 주제</h2>
          <p className="text-slate-400 text-center mb-8">
            매주 새로운 주제가 업데이트됩니다
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            {[
              '💼 주 4일제 도입',
              '🤖 AI 규제',
              '📚 대학 무상교육',
              '🏠 부동산 정책',
              '🎮 게임 셧다운제',
              '🚗 자율주행차',
              '💰 기본소득',
              '🌍 기후변화 대응',
            ].map((topic) => (
              <span
                key={topic}
                className="px-4 py-2 bg-slate-700/50 rounded-full text-sm"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">지금 바로 시작하세요!</h2>
          <p className="text-slate-400 mb-8">
            회원가입 없이 게스트로도 체험할 수 있어요
          </p>
          <button
            onClick={handleStart}
            className="btn-primary text-lg px-8 py-4 rounded-xl"
          >
            게임 시작하기 →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400 mb-4">
            <button onClick={() => navigate('/about')} className="hover:text-white transition-colors">
              서비스 소개
            </button>
            <button onClick={() => navigate('/faq')} className="hover:text-white transition-colors">
              자주 묻는 질문
            </button>
            <button onClick={() => navigate('/guidelines')} className="hover:text-white transition-colors">
              커뮤니티 가이드라인
            </button>
            <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">
              이용약관
            </button>
            <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">
              개인정보처리방침
            </button>
          </div>
          <p className="text-center text-slate-500 text-xs">
            © 2024 말싸움. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
